/**
 * Security Configuration Schema Validator
 * Validates and sanitizes configuration objects to prevent injection attacks
 */

// Configuration schema definitions
const CONFIG_SCHEMAS = {
    // Text type validation
    TEXT_TYPE: {
        LOCALIZED: 'Localized',
        INTERNAL: 'Internal',
        LITERAL: 'Literal'
    },

    // Provider validation
    SUPPORTED_PROVIDERS: ['email', 'phone'],

    // Country code validation (ISO 3166-1 alpha-2)
    VALID_COUNTRY_CODES: ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'IT', 'ES', 'NL', 'IL'],

    // Theme mode validation
    THEME_MODES: ['light', 'dark'],

    // Component types validation
    VALID_COMPONENT_TYPES: [
        'Simple', 'Accordion', 'CardGrid', 'SplitPane',
        'Stepper', 'Timeline', 'UpperLabeledTabs', 'CompositeSections',
        'NotFound'
    ]
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
const sanitizeHTML = (input) => {
    if (typeof input !== 'string') return input;

    // Remove script tags and their content
    let sanitized = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Remove javascript: protocol
    sanitized = sanitized.replace(/javascript:/gi, '');

    // Remove on* event handlers
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');

    // Remove data: protocol (except safe image types)
    sanitized = sanitized.replace(/data:(?!image\/(png|jpg|jpeg|gif|svg|webp))[^;,]*[;,]/gi, '');

    return sanitized;
};

/**
 * Validates text object structure
 */
const validateTextObject = (textObj, fieldName = 'text') => {
    if (!textObj || typeof textObj !== 'object') {
        throw new Error(`Invalid ${fieldName}: must be an object`);
    }

    if (!textObj.type || !Object.values(CONFIG_SCHEMAS.TEXT_TYPE).includes(textObj.type)) {
        throw new Error(`Invalid ${fieldName}.type: must be one of ${Object.values(CONFIG_SCHEMAS.TEXT_TYPE).join(', ')}`);
    }

    if (!textObj.text || typeof textObj.text !== 'string') {
        throw new Error(`Invalid ${fieldName}.text: must be a non-empty string`);
    }

    // Sanitize text content
    textObj.text = sanitizeHTML(textObj.text);

    return textObj;
};

/**
 * Validates color values to prevent CSS injection
 */
const validateColor = (color, fieldName = 'color') => {
    if (!color || typeof color !== 'string') {
        throw new Error(`Invalid ${fieldName}: must be a string`);
    }

    // Allow hex colors, rgb/rgba, hsl/hsla, and named colors
    const colorRegex = /^(#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})|rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)|hsl\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*\)|hsla\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*,\s*[\d.]+\s*\)|[a-zA-Z]+)$/;

    if (!colorRegex.test(color)) {
        throw new Error(`Invalid ${fieldName}: invalid color format`);
    }

    // Prevent javascript: or data: protocols in colors
    const lowerColor = color.toLowerCase();
    const hasJavaScript = lowerColor.indexOf('javascript') !== -1 && lowerColor.indexOf(':') !== -1;
    const hasDataProtocol = lowerColor.indexOf('data') !== -1 && lowerColor.indexOf(':') !== -1;
    if (hasJavaScript || hasDataProtocol) {
      throw new Error(`Invalid ${fieldName}: potentially malicious color value`);
    }    return color;
};

/**
 * Validates URL to prevent malicious redirects
 */
const validateURL = (url, fieldName = 'url') => {
    if (!url || typeof url !== 'string') {
        throw new Error(`Invalid ${fieldName}: must be a string`);
    }

    // Allow relative URLs and safe protocols
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
        return url; // Relative URLs are safe
    }

    try {
        const urlObj = new URL(url);
        const allowedProtocols = ['http:', 'https:', 'mailto:'];

        if (!allowedProtocols.includes(urlObj.protocol)) {
            throw new Error(`Invalid ${fieldName}: unsupported protocol ${urlObj.protocol}`);
        }

        return url;
    } catch (error) {
        throw new Error(`Invalid ${fieldName}: malformed URL`);
    }
};

/**
 * Validates application configuration
 */
const validateApplication = (application) => {
    if (!application || typeof application !== 'object') {
        throw new Error('Invalid application: must be an object');
    }

    // Validate application name
    if (!application.name || (typeof application.name !== 'string' && typeof application.name !== 'object')) {
        throw new Error('Invalid application.name: must be a non-empty string');
    }
    application.name = sanitizeHTML(application.name);

    // Validate logo URL if present
    if (application.logo) {
        application.logo = validateURL(application.logo, 'application.logo');
    }

    // Validate auth configuration
    if (application.auth) {
        validateAuthConfig(application.auth);
    }

    // Validate theme configuration
    if (application.theme) {
        validateThemeConfig(application.theme);
    }

    return application;
};

/**
 * Validates authentication configuration
 */
const validateAuthConfig = (auth) => {
    if (!auth || typeof auth !== 'object') {
        throw new Error('Invalid auth: must be an object');
    }

    // Validate enableRegister
    if (auth.enableRegister !== undefined && typeof auth.enableRegister !== 'boolean') {
        throw new Error('Invalid auth.enableRegister: must be a boolean');
    }

    // Validate verificationRequired
    if (auth.verificationRequired !== undefined && typeof auth.verificationRequired !== 'boolean') {
        throw new Error('Invalid auth.verificationRequired: must be a boolean');
    }

    // Validate enabled providers
    if (auth.enabledProviders) {
        if (!Array.isArray(auth.enabledProviders)) {
            throw new Error('Invalid auth.enabledProviders: must be an array');
        }

        auth.enabledProviders.forEach(provider => {
            if (!CONFIG_SCHEMAS.SUPPORTED_PROVIDERS.includes(provider)) {
                throw new Error(`Invalid provider: ${provider}. Must be one of ${CONFIG_SCHEMAS.SUPPORTED_PROVIDERS.join(', ')}`);
            }
        });
    }

    // Validate enabled country codes
    if (auth.enabledCountryCodes) {
        if (!Array.isArray(auth.enabledCountryCodes)) {
            throw new Error('Invalid auth.enabledCountryCodes: must be an array');
        }

        auth.enabledCountryCodes.forEach(code => {
            if (!CONFIG_SCHEMAS.VALID_COUNTRY_CODES.includes(code)) {
                throw new Error(`Invalid country code: ${code}. Must be one of ${CONFIG_SCHEMAS.VALID_COUNTRY_CODES.join(', ')}`);
            }
        });
    }

    // Validate roles assigned
    if (auth.rolesAssigned) {
        if (!Array.isArray(auth.rolesAssigned)) {
            throw new Error('Invalid auth.rolesAssigned: must be an array');
        }

        auth.rolesAssigned.forEach(role => {
            if (typeof role !== 'string') {
                throw new Error('Invalid role: must be a string');
            }
        });
    }

    return auth;
};

/**
 * Validates theme configuration
 */
const validateThemeConfig = (theme) => {
    if (!theme || typeof theme !== 'object') {
        throw new Error('Invalid theme: must be an object');
    }

    // Validate theme mode
    if (theme.mode && !CONFIG_SCHEMAS.THEME_MODES.includes(theme.mode)) {
        throw new Error(`Invalid theme.mode: must be one of ${CONFIG_SCHEMAS.THEME_MODES.join(', ')}`);
    }

    // Validate primary colors
    if (theme.primary) {
        if (theme.primary.main) {
            theme.primary.main = validateColor(theme.primary.main, 'theme.primary.main');
        }
        if (theme.primary.dark) {
            theme.primary.dark = validateColor(theme.primary.dark, 'theme.primary.dark');
        }
        if (theme.primary.light) {
            theme.primary.light = validateColor(theme.primary.light, 'theme.primary.light');
        }
    }

    // Validate secondary colors
    if (theme.secondary) {
        if (theme.secondary.main) {
            theme.secondary.main = validateColor(theme.secondary.main, 'theme.secondary.main');
        }
    }

    return theme;
};

/**
 * Validates page configuration
 */
const validatePages = (pages) => {
    if (!Array.isArray(pages)) {
        throw new Error('Invalid pages: must be an array');
    }

    const seenPaths = new Set();

    pages.forEach((page, index) => {
        if (!page || typeof page !== 'object') {
            throw new Error(`Invalid page at index ${index}: must be an object`);
        }

        // Validate path
        if (!page.path || typeof page.path !== 'string') {
            throw new Error(`Invalid page.path at index ${index}: must be a non-empty string`);
        }

        // Check for duplicate paths
        if (seenPaths.has(page.path)) {
            throw new Error(`Duplicate page path: ${page.path}`);
        }
        seenPaths.add(page.path);

        // Validate spec
        if (!page.spec || typeof page.spec !== 'object') {
            throw new Error(`Invalid page.spec at index ${index}: must be an object`);
        }

        if (!page.spec.type || !CONFIG_SCHEMAS.VALID_COMPONENT_TYPES.includes(page.spec.type)) {
            throw new Error(`Invalid page.spec.type at index ${index}: must be one of ${CONFIG_SCHEMAS.VALID_COMPONENT_TYPES.join(', ')}`);
        }

        // Validate auth if present
        if (page.auth) {
            if (typeof page.auth !== 'object') {
                throw new Error(`Invalid page.auth at index ${index}: must be an object`);
            }

            if (page.auth.enabled !== undefined && typeof page.auth.enabled !== 'boolean') {
                throw new Error(`Invalid page.auth.enabled at index ${index}: must be a boolean`);
            }

            if (page.auth.roles && !Array.isArray(page.auth.roles)) {
                throw new Error(`Invalid page.auth.roles at index ${index}: must be an array`);
            }
        }
    });

    return pages;
};

/**
 * Validates dashboard configuration
 */
const validateDashboards = (dashboards) => {
    if (!Array.isArray(dashboards)) {
        throw new Error('Invalid dashboards: must be an array');
    }

    const seenPaths = new Set();

    dashboards.forEach((dashboard, index) => {
        if (!dashboard || typeof dashboard !== 'object') {
            throw new Error(`Invalid dashboard at index ${index}: must be an object`);
        }

        // Validate path
        if (!dashboard.path || typeof dashboard.path !== 'string') {
            throw new Error(`Invalid dashboard.path at index ${index}: must be a non-empty string`);
        }

        // Check for duplicate paths
        if (seenPaths.has(dashboard.path)) {
            throw new Error(`Duplicate dashboard path: ${dashboard.path}`);
        }
        seenPaths.add(dashboard.path);

        // Validate name
        if (dashboard.name) {
            validateTextObject(dashboard.name, `dashboard.name at index ${index}`);
        }

        // Validate roles
        if (dashboard.roles && !Array.isArray(dashboard.roles)) {
            throw new Error(`Invalid dashboard.roles at index ${index}: must be an array`);
        }

        // Validate items
        if (dashboard.items && Array.isArray(dashboard.items)) {
            dashboard.items.forEach((item, itemIndex) => {
                validateDashboardItem(item, `dashboard[${index}].items[${itemIndex}]`);
            });
        }
    });

    return dashboards;
};

/**
 * Validates dashboard item configuration
 */
const validateDashboardItem = (item, path) => {
    if (!item || typeof item !== 'object') {
        throw new Error(`Invalid ${path}: must be an object`);
    }

    // Validate path
    if (!item.path || typeof item.path !== 'string') {
        throw new Error(`Invalid ${path}.path: must be a non-empty string`);
    }

    // Validate name
    if (item.name) {
        validateTextObject(item.name, `${path}.name`);
    }

    // Validate hide flag
    if (item.hide !== undefined && typeof item.hide !== 'boolean') {
        throw new Error(`Invalid ${path}.hide: must be a boolean`);
    }

    // Validate spec or specs
    if (item.spec) {
        if (!item.spec.type || !CONFIG_SCHEMAS.VALID_COMPONENT_TYPES.includes(item.spec.type)) {
            throw new Error(`Invalid ${path}.spec.type: must be one of ${CONFIG_SCHEMAS.VALID_COMPONENT_TYPES.join(', ')}`);
        }
    } else if (item.specs) {
        if (!Array.isArray(item.specs)) {
            throw new Error(`Invalid ${path}.specs: must be an array`);
        }

        item.specs.forEach((spec, specIndex) => {
            validateDashboardItem(spec, `${path}.specs[${specIndex}]`);
        });
    }
};

/**
 * Main configuration validator
 */
const validateConfiguration = (config) => {
    try {
        if (!config || typeof config !== 'object') {
            throw new Error('Configuration must be an object');
        }

        const validatedConfig = { ...config };

        // Validate application (required)
        if (!validatedConfig.application) {
            throw new Error('application configuration is required');
        }
        validatedConfig.application = validateApplication(validatedConfig.application);

        // Validate pages (optional)
        if (validatedConfig.pages) {
            validatedConfig.pages = validatePages(validatedConfig.pages);
        }

        // Validate dashboards (optional)
        if (validatedConfig.dashboards) {
            validatedConfig.dashboards = validateDashboards(validatedConfig.dashboards);
        }

        // Validate languages (optional)
        if (validatedConfig.languages) {
            if (!validatedConfig.languages.default || typeof validatedConfig.languages.default !== 'string') {
                throw new Error('Invalid languages.default: must be a string');
            }

            if (!Array.isArray(validatedConfig.languages.list)) {
                throw new Error('Invalid languages.list: must be an array');
            }
        }

        return validatedConfig;

    } catch (error) {
        console.error('Configuration validation failed:', error.message);
        throw new Error(`Configuration validation failed: ${error.message}`);
    }
};

/**
 * Runtime component type validator
 */
const validateComponentType = (type) => {
    if (!type || typeof type !== 'string') {
        throw new Error('Component type must be a non-empty string');
    }

    if (!CONFIG_SCHEMAS.VALID_COMPONENT_TYPES.includes(type)) {
        throw new Error(`Invalid component type: ${type}. Must be one of ${CONFIG_SCHEMAS.VALID_COMPONENT_TYPES.join(', ')}`);
    }

    return type;
};

/**
 * Sanitizes component props to prevent XSS and other attacks
 */
const sanitizeProps = (spec) => {
    if (!spec || typeof spec !== 'object') {
        return {};
    }

    const sanitizedSpec = { ...spec };

    // Sanitize string properties
    Object.keys(sanitizedSpec).forEach(key => {
        if (typeof sanitizedSpec[key] === 'string') {
            sanitizedSpec[key] = sanitizeHTML(sanitizedSpec[key]);
        } else if (sanitizedSpec[key] && typeof sanitizedSpec[key] === 'object' && sanitizedSpec[key].text) {
            // Handle text objects
            if (typeof sanitizedSpec[key].text === 'string') {
                sanitizedSpec[key].text = sanitizeHTML(sanitizedSpec[key].text);
            }
        }
    });

    return sanitizedSpec;
};

export {
    validateConfiguration,
    validateComponentType,
    validateTextObject,
    validateColor,
    validateURL,
    sanitizeHTML,
    sanitizeProps,
    CONFIG_SCHEMAS
};